import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, ShoppingBag, Grid3x3, Heart, Package, Info, Mail, HelpCircle } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const location = useLocation();
  const { storeInfo } = useStoreInfo();

  const mainNavigation = [
    { name: 'Home', href: '/home', icon: Home },
    { name: 'Products', href: '/products', icon: ShoppingBag },
    { name: 'Categories', href: '/categories', icon: Grid3x3 },
    { name: 'Wishlist', href: '/wishlist', icon: Heart },
    { name: 'Track Order', href: '/track', icon: Package },
  ];

  const supportLinks = [
    { name: 'About Us', href: '/about', icon: Info },
    { name: 'Contact Us', href: '/contact', icon: Mail },
    { name: 'FAQ', href: '/faq', icon: HelpCircle },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />

          {/* Drawer - Slides from top to bottom */}
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 left-0 right-0 z-50 bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-500 p-6 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-white font-display font-bold text-xl">Menu</h2>
                {storeInfo?.slogan && (
                  <p className="text-brand-50 text-sm font-light mt-1">{storeInfo.slogan}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation Content */}
            <div className="p-4 space-y-6">
              {/* Main Navigation */}
              <nav className="space-y-2">
                {mainNavigation.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className={`flex items-center gap-4 px-4 py-3 rounded-2xl font-semibold transition-all ${
                        isActive(item.href)
                          ? 'bg-gradient-to-r from-brand-50 to-brand-100 text-brand-700 shadow-md'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`p-2 rounded-lg transition-all ${isActive(item.href) ? 'bg-brand-200' : 'bg-gray-100'}`}>
                        <item.icon className={`h-5 w-5 ${isActive(item.href) ? 'text-brand-700' : 'text-gray-500'}`} />
                      </div>
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-brand-200 to-transparent" />

              {/* Support Links */}
              <div>
                <h3 className="px-4 mb-3 text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500 uppercase tracking-wider">
                  Support & Info
                </h3>
                <nav className="space-y-2">
                  {supportLinks.map((item, idx) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + idx * 0.04 }}
                    >
                      <Link
                        to={item.href}
                        onClick={onClose}
                        className={`flex items-center gap-4 px-4 py-3 rounded-2xl font-semibold transition-all ${
                          isActive(item.href)
                            ? 'bg-gradient-to-r from-brand-50 to-brand-100 text-brand-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`p-2 rounded-lg transition-all ${isActive(item.href) ? 'bg-brand-200' : 'bg-gray-100'}`}>
                          <item.icon className={`h-5 w-5 ${isActive(item.href) ? 'text-brand-700' : 'text-gray-500'}`} />
                        </div>
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}
                </nav>
              </div>

              {/* Contact Info */}
              {(storeInfo?.contact_email || storeInfo?.phone_num) && (
                <>
                  <div className="h-px bg-gradient-to-r from-transparent via-brand-200 to-transparent" />
                  <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl p-4">
                    {storeInfo?.phone_num && (
                      <p className="text-sm text-gray-700 mb-2">
                        <span className="font-semibold">Phone:</span> {storeInfo.phone_num}
                      </p>
                    )}
                    {storeInfo?.contact_email && (
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Email:</span> {storeInfo.contact_email}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
